//Assets imports

import { CheckIcon, PencilIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { deleteRoverTdm, patchRoverTdm } from '@/api/Rover/applicationTDM';
import type { IGetTasksResData, IPatchTdmPayload } from '@/api/Rover/applicationTDM/types';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import Input from '@/components/UI/Form/Input';
import Select from '@/components/UI/Form/Select';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationTeamMembers } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import type { GetVendorsResponse } from '@/types/Vendor';
import { taskStatus } from '../../../../../../../constants/Project';
import { TdmReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import { useTitleById } from '../../../../../../../hooks/ReactQueryHooks/useTitlesQuery';
import { notifyUser } from '../../../../../../../store/Toast';
import type { TdmMemberInterface } from '../../../../../../../types/Tdm';
import { formatDate3, getInitials, isObjectEmpty, sanitizeResponseError } from '../../../../../../../utils';

interface TaskRowInterface extends IGetTasksResData {
  isEditing: boolean;
}

export default function DueDiligenceTaskRow({ task, vendors }: { task: IGetTasksResData; vendors?: GetVendorsResponse[] }) {
  const { applicationId, applicationFormId } = useParams();
  const navigate = useNavigate();
  const _location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [taskRow, setTaskRow] = useState<TaskRowInterface>({
    ...task,
    isEditing: false,
  });

  const [inputErrors, setInputErrors] = useState({
    name: '',
    dueDate: '',
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [touchedInput, setTouchedInput] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    setTaskRow({ ...task, isEditing: false });
  }, [task]);

  const { getTitle } = useTitleById();

  const getApplicationTeamMembers = useApplicationTeamMembers({
    applicationId,
  });
  const applicationTeamMembers = getApplicationTeamMembers?.data?.data;
  const teams = applicationTeamMembers?.members?.map((member: any) => member?.user);

  const patchTdm = useMutation(
    (data: Partial<IPatchTdmPayload>) =>
      patchRoverTdm({
        tdmType: 'task',
        tdmId: taskRow.id,
        data,
      }),
    {
      onSuccess: () => {
        editRowHandler();
        queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_TASKS]);
        setTouchedInput({});
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Task saved successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const errorLocation = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `Task update failed. Invalid ${errorLocation}`,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const removeTdm = useMutation((tdmId: number) => deleteRoverTdm({ tdmType: 'task', tdmId }), {
    onSuccess: () => {
      queryClient.invalidateQueries([TdmReactQueryKey.GET_APPLICATION_TASKS]);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Task delete successful',
          toastType: 'success',
        })
      );
      setShowDeleteConfirm(false);
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const cancelEditHandler = () => {
    setTaskRow({ ...task, isEditing: false });
  };

  const editRowHandler = () => {
    setTaskRow((row: TaskRowInterface) => ({
      ...row,
      isEditing: !row.isEditing,
    }));
  };

  const rowInputChangeHandler = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value;

    if (e.target.name === 'lead' || e.target.name === 'support') {
      newValue = teams.find((mem: TdmMemberInterface) => mem.id === parseInt(newValue, 10));
    }

    if (e.target.name === 'vendor' && vendors) {
      const vendor = vendors?.find((mem) => mem.id === parseInt(newValue, 10));

      if (vendor) {
        newValue = vendor;
      }
    }

    if (e.target.name === 'name') {
      if (e.target.value.trim() === '') {
        setInputErrors((prevState) => ({
          ...prevState,
          name: 'Task name is required',
        }));
      } else if (e.target.value.length > 250) {
        setInputErrors((prevState) => ({
          ...prevState,
          name: 'Must be less than 250 characters',
        }));
      } else {
        setInputErrors((prevState) => ({
          ...prevState,
          name: '',
        }));
      }
    }

    setTouchedInput((prevState) => ({
      ...prevState,
      [e.target.name]: true,
    }));
    setTaskRow((row: TaskRowInterface) => ({
      ...row,
      [e.target.name]: newValue,
    }));
  };

  const saveTdmHandler = () => {
    const data: Partial<IPatchTdmPayload> = {
      ...(touchedInput?.name && { name: taskRow.name }),
      ...(touchedInput?.status && { status: taskRow.status }),
      ...(touchedInput?.lead && taskRow.lead && { leadId: taskRow.lead.id }),
      ...(touchedInput?.support && taskRow.support && { supportId: taskRow.support.id }),
      ...(touchedInput?.vendor && taskRow.vendor && { vendorId: taskRow.vendor.id }),
    };

    if (inputErrors?.name.length === 0) {
      if (!isObjectEmpty(data)) {
        patchTdm.mutate(data);
      } else {
        editRowHandler();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'No changes were detected to save the task.',
            toastType: 'info',
          })
        );
      }
    }
  };

  const deleteTaskHandler = () => {
    setShowDeleteConfirm(true);
  };

  const deleteConfirmHandler = () => {
    removeTdm.mutate(taskRow.id);
  };

  const deleteCancelHandler = () => {
    setShowDeleteConfirm(false);
  };

  const tdmDetailViewHandler = () => {
    if (!applicationFormId || !applicationId) {
      return;
    }

    navigate(
      buildRoute(AppRoutes.ROVER_APPLICATION_FORM_APPLICATION_TASK, {
        applicationFormId,
        applicationId,
        taskId: task.id,
      })
    );
  };

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title='Delete Task'
          message={`You are about to delete ${taskRow?.name}. Are you sure you want to delete ?`}
          acceptButtonLabel={`${removeTdm.isLoading ? 'Deleting...' : 'Delete'}`}
          declineButtonLabel='Cancel'
          accept={deleteConfirmHandler}
          decline={deleteCancelHandler}
        />
      )}
      <tr
        key={task.id}
        className={'flex items-center w-full justify-between px-5 py-4 border-b border-gray-200 text-sm hover:bg-green-50 cursor-pointer'}
      >
        {!taskRow.isEditing && (
          <td className='w-72'>
            <div className='flex flex-col underline' onClick={tdmDetailViewHandler}>
              <span>{taskRow.name}</span>
            </div>
          </td>
        )}
        {taskRow.isEditing && (
          <td className='w-72'>
            <div className='relative'>
              <Input type='text' defaultValue={taskRow.name} name='name' onChange={rowInputChangeHandler} />
              <span className='absolute text-xs text-tertiaryColor-normal'>{inputErrors?.name}</span>
            </div>
          </td>
        )}
        <td className='w-32'>
          <div className=''>
            {!taskRow.isEditing && <Badge status={taskRow.status} text={taskStatus[taskRow.status]} />}
            {taskRow.isEditing && (
              <Select name='status' defaultValue={taskRow.status} onChange={rowInputChangeHandler}>
                {Object.keys(taskStatus).map((key: string) => (
                  <option key={key} value={key}>
                    {taskStatus[key]}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </td>
        <td className='w-36'>
          {!taskRow.isEditing && taskRow.lead && (
            <Link to={`/app/administration/users/${taskRow?.lead?.id}`}>
              <div className='flex items-center gap-2'>
                <ImageCircularAvatar imageUrl={taskRow.lead.profileImageUrl} fallbackText={getInitials(taskRow.lead.name)} />
                <span>{taskRow.lead.name}</span>
              </div>
            </Link>
          )}
          {taskRow.isEditing && teams.length === 0 && "There aren't any external Project Members"}
          {taskRow.isEditing && teams.length > 0 && (
            <Select name='lead' defaultValue={taskRow.lead ? taskRow.lead.id : ''} onChange={rowInputChangeHandler}>
              {teams?.map((user: TdmMemberInterface) => {
                if (user.id !== taskRow?.support?.id) {
                  return (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  );
                }
                return <></>;
              })}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!taskRow.isEditing && taskRow.support && (
            <Link to={`/app/administration/users/${taskRow?.support?.id}`}>
              <div className='flex items-center gap-2'>
                <ImageCircularAvatar imageUrl={taskRow.support.profileImageUrl} fallbackText={getInitials(taskRow.support.name)} />
                <span>{taskRow.support.name}</span>
              </div>
            </Link>
          )}
          {!taskRow.isEditing && !taskRow.support && (
            <span>
              {taskRow.metaData.supportTitleId && getTitle(taskRow.metaData.supportTitleId)?.name}
              {taskRow.metaData.support && taskRow.metaData.support}
              {!taskRow.metaData.supportTitleId && !taskRow.metaData.support && 'Unassigned'}
            </span>
          )}
          {taskRow.isEditing && teams.length === 0 && "There aren't any external Project Members"}
          {taskRow.isEditing && teams.length > 0 && (
            <Select name='support' defaultValue={taskRow.support ? taskRow.support.id : ''} onChange={rowInputChangeHandler}>
              {!taskRow.support && <option>Select Support</option>}
              {teams?.map((user: TdmMemberInterface) => {
                if (user.id !== taskRow?.lead?.id) {
                  return (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  );
                }
                return <></>;
              })}
            </Select>
          )}
        </td>
        <td className='w-40'>
          {!taskRow.isEditing && taskRow?.vendor && <Link to='/app/vendor'>{taskRow?.vendor?.name}</Link>}

          {!taskRow.isEditing && !taskRow?.vendor && 'Unassigned'}

          {taskRow.isEditing && (
            <Select name='vendor' defaultValue={taskRow?.vendor?.id} onChange={rowInputChangeHandler}>
              {!taskRow.vendor && <option>Select Vendor</option>}
              {vendors?.map((user) => {
                return (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                );
              })}
            </Select>
          )}
        </td>
        {!taskRow.isEditing && <td className={'w-24'}>{taskRow.dueDate ? formatDate3(taskRow.dueDate) : 'None'}</td>}
        {taskRow.isEditing && (
          <td className={'w-36 relative'}>
            <Input
              type='date'
              defaultValue={taskRow.dueDate && formatDate3(taskRow.dueDate, 'yyyy-mm-dd')}
              name='dueDate'
              min={formatDate3(taskRow.startDate, 'yyyy-mm-dd')}
              onChange={rowInputChangeHandler}
            />
            {inputErrors?.dueDate && <span className='absolute text-xs text-tertiaryColor-normal'>{inputErrors.dueDate}</span>}
          </td>
        )}
        {!taskRow.isEditing && (
          <td className='w-36 flex gap-2 justify-center'>
            <div
              id='edit-button'
              className='rounded-full p-2 text-neutralColor-normal hover:bg-neutralColor-lighter hover:text-primaryColor-normal transition duration-300 ease-in-out'
              onClick={editRowHandler}
            >
              <PencilIcon className='h-5 w-5' />
            </div>
            <div
              id='delete-button'
              className='rounded-full p-2 text-neutralColor-normal hover:text-tertiaryColor-normal hover:bg-neutralColor-lighter transition duration-300 ease-in-out'
              onClick={deleteTaskHandler}
            >
              <TrashIcon className='h-5 w-5' />
            </div>
          </td>
        )}
        {taskRow.isEditing && (
          <td className='flex gap-4'>
            <div
              id='save-button'
              className='bg-gray-100 rounded-full p-1 text-primaryColor-normal hover:bg-primaryColor-lightest transition duration-300 ease-in-out'
              onClick={saveTdmHandler}
            >
              <CheckIcon className='h-5 w-5' />
            </div>
            <div
              id='cancel-button'
              className='bg-gray-100 rounded-full p-1 text-tertiaryColor-normal hover:bg-tertiaryColor-lightest transition duration-300 ease-in-out'
              onClick={cancelEditHandler}
            >
              <XCircleIcon className='h-5 w-5' />
            </div>
          </td>
        )}
      </tr>
    </>
  );
}
